import type Delta from "quill-delta";
import { EditorCalc } from "./editorCalc";
import { QuillInstance } from "./quillInstance";
import { QuillTransformer } from "./quillTransformer";
import { TextOverlay } from "./textOverlay";
import { TextResizer } from "./textResizer";
import type {
    LimitNoticeKind,
    OverlayStyles,
    QuillFormats,
    ResizeOptions,
    ResizeResult,
    SelectionSummary,
    TextAlign,
    TextChangeMeta,
    TextVerticalAlign,
} from "./typesV2";

interface QuillEditorArgs {
    overlayRoot: HTMLElement;
    onSize: ({ width, height, mode, fixedWidth }: ResizeResult) => void;
    onSelectionChange: (summary: SelectionSummary) => void;
    onTextChange?: (meta: TextChangeMeta) => void;
    onFontSizeChange: (fontSize: number) => void;
    onPasteApplied?: () => void;
    getCurrentTextData: () => {
        fontFamily: string;
        fontSize: number;
        textAlign: TextAlign;
        textVerticalAlign: TextVerticalAlign;
        delta: Delta;
    };
}

export class QuillEditor {
    readonly overlay: TextOverlay;
    readonly quill: QuillInstance;
    readonly transformer: QuillTransformer;
    readonly editorCalc: EditorCalc;
    readonly textResizer: TextResizer;
    readonly onSelectionChange: (summary: SelectionSummary) => void;
    readonly onTextChange?: (meta: TextChangeMeta) => void;

    private active = false;

    onKeyDownCapture?: (e: KeyboardEvent) => void;
    onKeyDown?: (e: KeyboardEvent) => void;
    onBlur?: (e: FocusEvent) => void;

    constructor({
        overlayRoot,
        onFontSizeChange,
        onPasteApplied,
        onSelectionChange,
        onTextChange,
        onSize,
        getCurrentTextData,
    }: QuillEditorArgs) {
        this.overlay = new TextOverlay(overlayRoot);
        this.quill = new QuillInstance(this.overlay.getFieldEl());
        this.transformer = new QuillTransformer(this.overlay.getTransformFieldEl());
        this.editorCalc = new EditorCalc();
        this.textResizer = new TextResizer(
            this.quill,
            this.overlay,
            this.editorCalc,
            onSize,
            onFontSizeChange,
            getCurrentTextData,
        );
        this.onSelectionChange = onSelectionChange;
        this.onTextChange = onTextChange;

        this.overlay.bindEditor(this.quill.getEditor());

        this.quill.onKeyDownCapture = (e) => this.onKeyDownCapture?.(e);
        this.quill.onKeyDown = (e) => this.onKeyDown?.(e);

        this.quill.onBlur = (e) => {
            if (this.onBlur) {
                this.onBlur(e);
            }
        };
        this.quill.onPasteApplied = onPasteApplied;
    }

    open(style: OverlayStyles, delta: Delta) {
        this.active = true;

        this.overlay.updateOverlay(style);
        this.overlay.show();

        this.quill.attachListeners((delta, meta) => {
            this.textResizer.onTextChange(delta, meta);
            this.onTextChange?.(meta);
        }, this.onSelectionChange);
        this.quill.load(delta);
    }

    updateTransform(options) {
        this.overlay.updateTransform(options);
    }

    flushStickerFit() {
        this.textResizer.flushStickerFit();
    }

    close() {
        this.active = false;
        this.quill.detachListeners();
        this.quill.clear();
        this.overlay.hide();
        this.textResizer.clear();
    }

    focusAtPoint(clientX: number, clientY: number): void {
        if (!this.active) {
            return;
        }

        this.quill.focusAtPoint(clientX, clientY);
    }

    focusToEnd(): void {
        if (!this.active) {
            return;
        }

        this.quill.focusToEnd();
    }

    focus(): void {
        if (!this.active) {
            return;
        }

        this.quill.focus();
    }

    async pasteFromClipboard(): Promise<void> {
        if (!this.active) {
            return;
        }

        await this.quill.pasteFromClipboard();
    }

    async copySelectionToClipboard(): Promise<void> {
        if (!this.active) {
            return;
        }

        await this.quill.copySelectionToClipboard();
    }

    deleteSelectionOrBackward(): void {
        if (!this.active) {
            return;
        }

        this.quill.deleteSelectionOrBackward();
    }

    getDelta() {
        return this.quill.getDelta();
    }

    convertHtmlToDeltaForPaste(html: string): Delta {
        return this.transformer.convertClipboardHtmlToDelta(html);
    }

    contains(node: Node) {
        return this.overlay.contains(node);
    }

    getIsTextChanged(): boolean {
        if (!this.active) {
            return false;
        }

        return this.quill.getIsTextChanged();
    }

    patchDelta(name: QuillFormats, value: string | boolean, delta: Delta) {
        return this.transformer.patch(name, value, delta);
    }

    isActive() {
        return this.active;
    }

    applyTextFormatting(name: QuillFormats, value: string | boolean): void {
        if (!this.active) {
            return;
        }

        this.quill.applyFormatting(name, value);
    }

    applyTextFormattingToAll(name: QuillFormats, value: string | boolean): void {
        if (!this.active) {
            return;
        }

        this.quill.applyFormattingToDocument(name, value);
    }

    setForcedColor(color: string | null): void {
        if (!this.active) {
            return;
        }

        if (color) {
            this.overlay.setTextColor(color);
        }

        this.quill.setForcedColor(color);
    }

    getSelection() {
        if (!this.active) {
            return;
        }

        return this.quill.getSelection();
    }

    selectAll() {
        this.quill.selectAll();
    }

    getLength() {
        return this.quill.getLength();
    }

    showSelectionRects(scale: number) {
        const range = this.quill.getSelection();

        if (range) {
            const rects = this.quill.getSelectionRects(range, scale);
            this.overlay.showSelectionRects(rects);
        }
    }

    hideSelectionRects() {
        this.overlay.clearSelectionRects();
        this.quill.setLastSelection();
    }

    setResizeOptions(newOptions: ResizeOptions) {
        this.textResizer.setResizeOptions(newOptions);
    }

    getQuill() {
        return this.quill.getQuill();
    }

    remeasure() {
        if (!this.active) {
            return;
        }

        this.textResizer.remeasure();
    }

    replaceSelectionWithText(text: string): void {
        if (!this.active) {
            return;
        }

        this.quill.replaceSelectionWithText(text, "user", "typing");
    }

    replaceSelectionWithDelta(delta: Delta, limitKind: LimitNoticeKind = "typing"): void {
        if (!this.active) {
            return;
        }

        this.quill.replaceSelectionWithDelta(delta, "user", limitKind);
    }
}
